import React, { useState, useEffect } from 'react';
import { Dropdown, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import './LanguageSwitcher.css';
import { LanguageService } from '../../Services/Axios/Api';

const LanguageSwitcher = ({ 
  variant = 'outline-light', 
  size = 'sm',
  className = '',
  showLanguageName = true,
  showNativeName = false,
  onLanguageChange
}) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  
  // Local storage keys
  const LANGUAGE_STORAGE_KEY = 'selectedLanguage';
  const DIRECTION_STORAGE_KEY = 'selectedDirection';
  
  const [availableLanguages, setAvailableLanguages] = useState([]);
  
  // Get languages from API and set initial language
  const getLanguages = async () => {
    try {
      const response = await LanguageService.getLanguages();
      console.log("languages", response);
      setAvailableLanguages(response.result);
      
      // Get stored language from localStorage or use default from API
      const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      const storedDirection = localStorage.getItem(DIRECTION_STORAGE_KEY);
      
      if (storedLanguage && storedDirection) {
        // Use stored language and direction
        const storedLangData = response.result.find(lang => lang.code === storedLanguage);
        if (storedLangData) {
          i18n.changeLanguage(storedLanguage);
          document.documentElement.dir = storedDirection;
          document.documentElement.lang = storedLanguage;
        } else {
          // Stored language not found in API, fallback to default
          setDefaultLanguage(response.result);
        }
      } else {
        // No stored language, use default from API
        setDefaultLanguage(response.result);
      }
    } catch (error) {
      console.log(error);
    }
  };
  
  // Set default language from API
  const setDefaultLanguage = (languages) => {
    const defaultLang = languages.find(lang => lang.isDefault === true);
    if (defaultLang) {
      i18n.changeLanguage(defaultLang.code);
      document.documentElement.dir = defaultLang.direction;
      document.documentElement.lang = defaultLang.code;

      // Store default language in localStorage
      localStorage.setItem(LANGUAGE_STORAGE_KEY, defaultLang.code);
      localStorage.setItem(DIRECTION_STORAGE_KEY, defaultLang.direction);
    }
  };
  
  // Utility function to clear stored language (useful for testing)
  const clearStoredLanguage = () => {
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    localStorage.removeItem(DIRECTION_STORAGE_KEY);
    console.log('Stored language cleared');
  };
  
  // Utility function to get current stored language info
  const getStoredLanguageInfo = () => {
    const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    const storedDir = localStorage.getItem(DIRECTION_STORAGE_KEY);
    return { language: storedLang, direction: storedDir };
  };

  // Update current language when i18n language changes
  useEffect(() => {
    getLanguages();
  }, []);
  
  // Debug: Log current language state
  useEffect(() => {
    console.log('Current language state:', {
      i18nLanguage: i18n.language,
      documentDir: document.documentElement.dir,
      documentLang: document.documentElement.lang,
      stored: getStoredLanguageInfo()
    });
  }, [i18n.language]);

  
  // Get current language info
  const getCurrentLanguageInfo = () => {
    return availableLanguages.find(lang => lang?.code === i18n.language) || availableLanguages[0];
  };

  const currentLangInfo = getCurrentLanguageInfo();

  // Handle language change
  const handleLanguageChange = (languageCode) => {
    const selectedLang = availableLanguages.find(lang => lang?.code === languageCode);
    if (selectedLang) {
      i18n.changeLanguage(languageCode);
      
      // Update document direction for RTL languages
      document.documentElement.dir = selectedLang.direction;
      document.documentElement.lang = languageCode;
      
      // Store selected language and direction in localStorage
      localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
      localStorage.setItem(DIRECTION_STORAGE_KEY, selectedLang.direction);
      
      // Notify parent component about language change
      if (onLanguageChange) {
        onLanguageChange(languageCode);
      }
      
      // console.log(`Language changed to: ${languageCode}, Direction: ${selectedLang.direction}`);
      window.location.reload();
    }
  };
  if(availableLanguages.length==0){
    return null;
  }
  return (
    <div className={`language-switcher ${className}`}>
      <Dropdown align="end" onSelect={handleLanguageChange}>
        <Dropdown.Toggle 
          variant={variant} 
          size={size}
          id="language-switcher-dropdown"
          className="language-switcher-toggle d-flex align-items-center gap-2"
        >
         {/* {showFlag && (
            <span className="language-flag">{currentLangInfo.flag}</span>
          )} */}
          
          {showLanguageName&& (
            <span className="language-name d-none d-md-inline">
              {currentLangInfo?.name}
            </span>
          )}
          
          <span className="language-code">{currentLangInfo?.code?.toUpperCase()}</span>
          
          <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
          
        </Dropdown.Toggle>

        <Dropdown.Menu className="language-switcher-menu">
          <Dropdown.Header className="language-menu-header" dir='ltr'>
            <FontAwesomeIcon icon={faGlobe} className="me-2" />
            <span>{t('LanguageSwitcher.SelectLanguage')}</span>
          </Dropdown.Header>
          
          <Dropdown.Divider />
          
          {availableLanguages.map((language) => (
            <Dropdown.Item
              key={language.code}
              eventKey={language.code}
              className={`language-menu-item ${i18n.language === language.code ? 'active' : ''}`}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="d-flex align-items-center gap-3">
                  
                  
                  <div className="language-info">
                    <div className="language-name">{language.name}</div>
                    {showNativeName && language.nativeName !== language.name && (
                      <small className="language-native text-muted">
                        {language.nativeName}
                      </small>
                    )}
                  </div>
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <span className="language-code-small">{language.code.toUpperCase()}</span>
                  {i18n.language === language.code && (
                    <FontAwesomeIcon icon={faCheck} className="text-success" />
                  )}
                </div>
              </div>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default LanguageSwitcher;
